// Cookie utilities for cross-subdomain authentication

const buildCookieString = ({
  name,
  value,
  expires,
  domain,
  path = "/",
}: {
  name: string;
  value: string;
  expires: Date;
  domain?: string | null;
  path?: string;
}): string => {
  const secureFlag = window.location.protocol === "https:" ? "secure; " : "";
  const domainPart = domain ? `domain=${domain}; ` : "";
  return `${name}=${value}; expires=${expires.toUTCString()}; path=${path}; ${domainPart}${secureFlag}samesite=lax`;
};

type TCookieLifetime =
  | number
  | {
      days?: number;
      maxAgeSeconds?: number;
      expiresAt?: Date;
    };

const resolveCookieExpiry = (lifetime: TCookieLifetime = 7): Date => {
  if (typeof lifetime === "number") {
    const expires = new Date();
    expires.setTime(expires.getTime() + lifetime * 24 * 60 * 60 * 1000);
    return expires;
  }

  if (lifetime.expiresAt instanceof Date) {
    return lifetime.expiresAt;
  }

  if (typeof lifetime.maxAgeSeconds === "number") {
    const expires = new Date();
    expires.setTime(expires.getTime() + lifetime.maxAgeSeconds * 1000);
    return expires;
  }

  const expires = new Date();
  expires.setTime(
    expires.getTime() + (lifetime.days ?? 7) * 24 * 60 * 60 * 1000,
  );
  return expires;
};

/**
 * Get token from URL query parameters
 * Used for localhost subdomain redirects where localStorage is isolated
 */
export const getTokenFromURL = (paramName: string): string | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get(paramName);
    if (value) {
      console.debug(`[Cookies] Retrieved from URL: ${paramName}`);
    }
    return value;
  } catch (e: any) {
    console.warn(`[Cookies] Failed to read from URL: ${e}`);
    return null;
  }
};

export const setSubdomainCookie = (
  name: string,
  value: string,
  lifetime: TCookieLifetime = 7,
) => {
  const expires = resolveCookieExpiry(lifetime);

  // Always set a host-only cookie, scoped strictly to the current
  // subdomain / host. This ensures that when logging in via a
  // specific tenant subdomain, we do NOT create cookies on the
  // "main"/root domain that could be shared with other tenants.
  document.cookie = buildCookieString({ name, value, expires, domain: null });
};

export const getCookie = (name: string): string | null => {
  const hostname = window.location.hostname;
  const isLocalhost =
    hostname === "localhost" ||
    hostname.endsWith(".localhost") ||
    hostname === "127.0.0.1";

  // For localhost, check URL params first (useful after redirect from main domain to subdomain)
  // This is critical because localStorage is isolated per subdomain
  if (isLocalhost) {
    const urlValue = getTokenFromURL(name);
    if (urlValue) {
      console.debug(`[Cookies] Found in URL, storing in localStorage: ${name}`);
      try {
        localStorage.setItem(name, urlValue);
        // Also try to set cookie for future requests (may not work cross-subdomain but worth trying)
        try {
          setSubdomainCookie(name, urlValue, name === "access_token" ? 7 : 30);
        } catch (e) {
          console.log("[Cookies] Failed:", e);
          // Cookie setting may fail for localhost, that's okay
        }
      } catch (e) {
        console.log("[Cookies] Failed to store URL token in localStorage:", e);
      }
      return urlValue;
    }

    // Check localStorage for current origin (works for same subdomain)
    try {
      const value = localStorage.getItem(name);
      if (value) {
        console.debug(`[Cookies] Retrieved from localStorage: ${name}`);
        return value;
      }
    } catch (e) {
      console.log("[Cookies] Failed to read from localStorage:", e);
    }
  }

  // Check document cookies (works for all domains, including cross-subdomain in production)
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(";");

  for (const cookie of cookies) {
    let c = cookie;
    while (c.startsWith(" ")) {
      c = c.substring(1);
    }
    if (c.startsWith(nameEQ)) {
      const value = c.substring(nameEQ.length);
      // console.debug(`[Cookies] Retrieved from document.cookie: ${name}`);
      // For localhost, also store in localStorage for faster access
      if (isLocalhost) {
        try {
          localStorage.setItem(name, value);
        } catch (e) {
          console.log(e);
          // Ignore localStorage errors
        }
      }
      return value;
    }
  }

  return null;
};

export const deleteSubdomainCookie = (name: string) => {
  const hostname = window.location.hostname;
  const isLocalhost =
    hostname === "localhost" ||
    hostname.endsWith(".localhost") ||
    hostname === "127.0.0.1";

  // For localhost, delete from localStorage
  if (isLocalhost) {
    try {
      localStorage.removeItem(name);
      console.debug(`[Cookies] Deleted from localStorage: ${name}`);
    } catch (e) {
      console.log("[Cookies] Failed to delete from localStorage:", e);
    }
  }
  const expires = new Date(0);

  // Delete host-only cookie (scoped to current subdomain/host).
  document.cookie = buildCookieString({
    name,
    value: "",
    expires,
    domain: null,
  });

  // Extra localhost fallbacks for any historical cookies that may have
  // been created on localhost-style domains.
  if (isLocalhost) {
    document.cookie = buildCookieString({
      name,
      value: "",
      expires,
      domain: "localhost",
    });
    document.cookie = buildCookieString({
      name,
      value: "",
      expires,
      domain: ".localhost",
    });
  }
  console.debug(`[Cookies] Deleted cookie: ${name}`);
};
